import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsBestPractices() {
  return (
    <DocsLayout>
      <DocsContent
        title="Best Practices"
        description="Tips and recommendations for getting the most out of XENDREL. Learn how to organize your investigations, optimize document processing, and improve your analysis workflow."
        previousPage={{ title: 'Natural Language Queries', href: '/docs/natural-language' }}
      >
        <H2 id="case-organization">Case Organization</H2>

        <H3 id="naming-conventions">Naming Conventions</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Use consistent, descriptive names for your cases:
        </p>

        <div className="space-y-3 my-4">
          <NamingExample
            good="2024-Q1-Financial-Fraud-Investigation"
            bad="Case 1"
            reason="Include date, type, and subject for easy identification"
          />
          <NamingExample
            good="Smith-Family-Corporate-Network"
            bad="John's case"
            reason="Use the primary subject matter, not personal references"
          />
        </div>

        <H3 id="case-descriptions">Case Descriptions</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Write detailed descriptions that include:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Scope</strong> - What's being investigated
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Key subjects</strong> - Primary entities of
              interest
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Time period</strong> - Relevant date ranges
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Objectives</strong> - What you're trying to find
              or prove
            </span>
          </li>
        </ul>

        <H3 id="one-investigation-per-case">One Investigation Per Case</H3>
        <DocsCallout variant="tip" title="Case Separation">
          Keep separate investigations in separate cases. Mixing unrelated investigations makes the
          knowledge graph harder to navigate and can create misleading connections.
        </DocsCallout>

        <H2 id="document-management">Document Management</H2>

        <H3 id="document-preparation">Document Preparation</H3>
        <p className="text-foreground mb-4 leading-relaxed">Before uploading documents:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Check quality</strong> - Ensure scanned documents
              are legible
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Remove passwords</strong> - Unlock
              password-protected files
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Name clearly</strong> - Use descriptive file names
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Check size</strong> - Split very large documents
              if over 50MB
            </span>
          </li>
        </ul>

        <H3 id="batch-similar-documents">Batch Similar Documents</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Upload documents of the same type together and select the appropriate pipeline. This
          ensures consistent extraction across similar documents.
        </p>

        <H3 id="review-after-processing">Review After Processing</H3>
        <p className="text-foreground mb-4 leading-relaxed">After documents are processed:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Check the extraction summary for any failures</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Spot-check key entities for accuracy</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Reprocess with a different pipeline if results are poor</span>
          </li>
        </ul>

        <H2 id="entity-resolution">Entity Resolution</H2>

        <H3 id="regular-resolution-reviews">Regular Resolution Reviews</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          As you add documents, duplicate entities may appear (e.g., "John Smith" and "J. Smith"
          referring to the same person). Use the Entity Resolution tool regularly to:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Review suggested matches</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Merge confirmed duplicates</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Dismiss false positives</span>
          </li>
        </ul>

        <DocsCallout variant="info" title="Entity Resolution Impact">
          Resolving entities correctly is crucial for accurate analysis. Unresolved duplicates can
          hide important connections, while incorrect merges can create false relationships.
        </DocsCallout>

        <H3 id="resolution-best-practices">Resolution Best Practices</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Always verify matches using source documents</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Look for multiple confirming data points (same address, phone, etc.)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>When in doubt, don't merge - keeping entities separate is safer</span>
          </li>
        </ul>

        <H2 id="graph-analysis">Graph Analysis</H2>

        <H3 id="start-broad-then-focus">Start Broad, Then Focus</H3>
        <p className="text-foreground mb-4 leading-relaxed">When exploring a new case:</p>

        <ol className="space-y-2 my-4 text-foreground list-decimal list-inside">
          <li>First, get an overview of the entire graph</li>
          <li>Identify clusters and highly connected nodes</li>
          <li>Focus on specific areas of interest</li>
          <li>Use sandboxes to isolate subgraphs for detailed analysis</li>
        </ol>

        <H3 id="use-multiple-views">Use Multiple Views</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Combine graph view and structured view for comprehensive analysis:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Graph view</strong> for relationship discovery and
              visual patterns
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Structured view</strong> for filtering, sorting,
              and property comparison
            </span>
          </li>
        </ul>

        <H3 id="document-your-findings">Document Your Findings</H3>
        <p className="text-foreground mb-4 leading-relaxed">As you analyze:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Create sandboxes to save important subgraphs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Take screenshots of key connections</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Note path findings between entities</span>
          </li>
        </ul>

        <H2 id="natural-language-queries">Natural Language Queries</H2>

        <H3 id="iterative-querying">Iterative Querying</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Build understanding through successive questions:
        </p>

        <ol className="space-y-2 my-4 text-foreground list-decimal list-inside">
          <li>Start with broad questions ("Who are the key people?")</li>
          <li>Follow up on interesting results</li>
          <li>Narrow down to specific connections or properties</li>
          <li>Verify findings by asking the same question differently</li>
        </ol>

        <H3 id="combine-with-visual-exploration">Combine with Visual Exploration</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Use queries and graph exploration together:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Query to find entities of interest</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Click results to view them in the graph</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Explore the visual neighborhood for context</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Query again to dive deeper</span>
          </li>
        </ul>

        <H2 id="performance-tips">Performance Tips</H2>

        <H3 id="large-cases">Working with Large Cases</H3>
        <p className="text-foreground mb-4 leading-relaxed">For cases with many entities:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Use filters to reduce the visible graph</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Work in sandboxes with focused subsets</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Use the structured view for browsing</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Query for specific entities rather than loading everything</span>
          </li>
        </ul>

        <H3 id="slow-queries">Handling Slow Queries</H3>
        <p className="text-foreground mb-4 leading-relaxed">If queries are running slowly:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Reinitialize indexes from case settings</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Be more specific in your queries</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Avoid very broad questions on large datasets</span>
          </li>
        </ul>

        <H2 id="security-considerations">Security Considerations</H2>

        <DocsCallout variant="warning" title="Data Sensitivity">
          XENDREL may contain sensitive investigation data. Follow your organization's security
          policies for handling confidential information.
        </DocsCallout>

        <H3 id="access-control">Access Control</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Cases are private to authenticated users</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Log out when leaving your workstation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Don't share login credentials</span>
          </li>
        </ul>

        <H3 id="data-handling">Data Handling</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Archive closed cases rather than deleting (maintains audit trail)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Be cautious when exporting data</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Follow document retention policies</span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}

function NamingExample({ good, bad, reason }: { good: string; bad: string; reason: string }) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-green-600 dark:text-green-400 text-xs font-medium">GOOD:</span>
          <code className="text-sm text-foreground bg-muted px-2 py-0.5 rounded">{good}</code>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-red-600 dark:text-red-400 text-xs font-medium">BAD:</span>
          <code className="text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded">{bad}</code>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{reason}</p>
    </Card>
  )
}
